import { useState } from 'react';
import { Modal, Form, Row, Col, Input, Button, notification } from 'antd';
import { useI18nLocalization } from '../../../i18nLocalization/useI18nLocalization';
import { useCategoriesLogic } from '../../useCategoriesLogic';
import { categoryName } from '../../../validation/regex';
import PropTypes from 'prop-types';

const CreateCategoryModal = ({ isVisible, onSaveCallback, onCloseCallback, categoryNames }) => {
	const categoriesLogic = useCategoriesLogic();
	const { t } = useI18nLocalization();
	const [form] = Form.useForm();
	const [isSaving, setIsSaving] = useState(false);

	const handleOnCancel = () => {
		setIsSaving(false);
		clearForm();
		onCloseCallback();
	};

	const clearForm = () => {
		form.resetFields();
	};

	const handleOnSave = async (formValues) => {
		setIsSaving(true);
		const categoryName = formValues.categoryName;
		const successful = await categoriesLogic.createCategory(categoryName);
		if (successful) {
			notification['success']({
				message: t(`New Category Created!`),
				duration: 5
			});

			clearForm();
			onSaveCallback();
		} else {
			notification['error']({
				message: t(
					`We encountered an error creating the category. Please try again. If it persists, please submit an EntDev Helpdesk ticket.`
				),
				duration: 10
			});
		}
		setIsSaving(false);
	};

	return (
		<Modal
			visible={isVisible}
			title={t(`Create New Category`)}
			okText={t('Save')}
			onOk={() => {
				form.submit();
			}}
			cancelText={t('Cancel')}
			onCancel={handleOnCancel}
			forceRender
			confirmLoading={isSaving}
			footer={[
				<Button key='cancel' onClick={handleOnCancel}>
					{t('Cancel')}
				</Button>,
				<Button
					key='save'
					onClick={() => {
						form.submit();
					}}
				>
					{t('Save')}
				</Button>
			]}
		>
			<Form form={form} layout='vertical' requiredMark scrollToFirstError onFinish={handleOnSave}>
				<Row>
					<Col span={24}>
						<Form.Item
							label={t('Category Name')}
							name='categoryName'
							rules={[
								{
									validator: (field, value) => {
										if (!value) {
											return Promise.reject(new Error(t('Name is required')));
										}
										if (categoryNames.includes(value)) {
											return Promise.reject(new Error(t('Name cannot be used by another category')));
										}
										if (!value.match(categoryName)) {
											return Promise.reject(new Error(t('Name must be alpha characters')));
										}
										return Promise.resolve();
									}
								}
							]}
						>
							<Input placeholder={t('category name')} maxLength={50} />
						</Form.Item>
					</Col>
				</Row>
			</Form>
		</Modal>
	);
};

CreateCategoryModal.propTypes = {
	isVisible: PropTypes.bool.isRequired,
	onCloseCallback: PropTypes.func.isRequired,
	onSaveCallback: PropTypes.func.isRequired,
	categoryNames: PropTypes.array.isRequired
};

export default CreateCategoryModal;
